/**
 * useEndpointGroupAccess Hook
 * Provides access to endpoint group data and organization access management
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  endpointGroupsQueryAtom,
  orgEndpointGroupsQueryAtom,
} from "@/store/adminDataStore";
import { adminAPI } from "@/services";
import type { IEndpointGroup, IOrgEndpointGroups } from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseEndpointGroupAccessReturn {
  /** All endpoint groups */
  endpointGroups: IEndpointGroup[];
  /** Organization endpoint group mappings */
  orgEndpointGroups: IOrgEndpointGroups[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Get endpoint groups for a specific organization */
  getOrgEndpointGroups: (orgId: number) => IEndpointGroup[];
  /** Check if organization has access to endpoint group */
  hasAccess: (orgId: number, endpointGroupId: number) => boolean;
  /** Grant organization access to endpoint group */
  addAccess: (orgId: number, endpointGroupId: number) => Promise<boolean>;
  /** Revoke organization access to endpoint group */
  removeAccess: (orgId: number, endpointGroupId: number) => Promise<boolean>;
  /** Updating state */
  isUpdating: boolean;
  /** Update error */
  updateError: Error | null;
}

export function useEndpointGroupAccess(): UseEndpointGroupAccessReturn {
  const [endpointGroupsQuery] = useAtom(endpointGroupsQueryAtom);
  const [orgEndpointGroupsQuery] = useAtom(orgEndpointGroupsQueryAtom);
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // All endpoint groups
  const endpointGroups = useMemo(
    () => endpointGroupsQuery.data ?? [],
    [endpointGroupsQuery.data],
  );

  // Organization endpoint group mappings
  const orgEndpointGroups = useMemo(
    () => orgEndpointGroupsQuery.data ?? [],
    [orgEndpointGroupsQuery.data],
  );

  // Map of org ID to endpoint group IDs for fast lookup
  const orgAccessMap = useMemo(() => {
    const map = new Map<number, Set<number>>();
    orgEndpointGroups.forEach((mapping) => {
      const groupIds = new Set(mapping.endpoint_groups.map((g) => g.id));
      map.set(mapping.organization_id, groupIds);
    });
    return map;
  }, [orgEndpointGroups]);

  // Get endpoint groups for a specific organization
  const getOrgEndpointGroups = useCallback(
    (orgId: number): IEndpointGroup[] => {
      const mapping = orgEndpointGroups.find(
        (m) => m.organization_id === orgId,
      );
      return mapping?.endpoint_groups ?? [];
    },
    [orgEndpointGroups],
  );

  // Check if organization has access to endpoint group
  const hasAccess = useCallback(
    (orgId: number, endpointGroupId: number): boolean => {
      const groupIds = orgAccessMap.get(orgId);
      return groupIds?.has(endpointGroupId) ?? false;
    },
    [orgAccessMap],
  );

  // Add access mutation
  const addAccess = useCallback(
    async (orgId: number, endpointGroupId: number): Promise<boolean> => {
      setIsUpdating(true);
      setUpdateError(null);

      try {
        await adminAPI.addEndpointGroupAccess({
          organization_id: orgId,
          endpoint_group_id: endpointGroupId,
        });
        await invalidateWithTimeout(queryClient, ["orgEndpointGroups"]);
        return true;
      } catch (error) {
        setUpdateError(error as Error);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient],
  );

  // Remove access mutation
  const removeAccess = useCallback(
    async (orgId: number, endpointGroupId: number): Promise<boolean> => {
      setIsUpdating(true);
      setUpdateError(null);

      try {
        await adminAPI.removeEndpointGroupAccess({
          organization_id: orgId,
          endpoint_group_id: endpointGroupId,
        });
        await invalidateWithTimeout(queryClient, ["orgEndpointGroups"]);
        return true;
      } catch (error) {
        setUpdateError(error as Error);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient],
  );

  // Invalidate queries - marks as stale and refetches active queries
  const refetch = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["endpointGroups"],
        refetchType: "active",
      }),
      queryClient.invalidateQueries({
        queryKey: ["orgEndpointGroups"],
        refetchType: "active",
      }),
    ]);
  }, [queryClient]);

  return {
    endpointGroups,
    orgEndpointGroups,
    isLoading:
      endpointGroupsQuery.isLoading || orgEndpointGroupsQuery.isLoading,
    error: endpointGroupsQuery.error ?? orgEndpointGroupsQuery.error ?? null,
    refetch,
    getOrgEndpointGroups,
    hasAccess,
    addAccess,
    removeAccess,
    isUpdating,
    updateError,
  };
}
